import React, { useState, useEffect, useRef } from 'react';
import { ExclamationCircleIcon, CheckCircleIcon, XCircleIcon, MinusCircleIcon } from '@heroicons/react/20/solid';
import ApiClient from '../utils/ApiClient';

function LocalInventoryAudit() {
    const [scanInput, setScanInput] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [locations, setLocations] = useState([]);
    const [oldestLocations, setOldestLocations] = useState([]); // audit-priority list
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [debounceTimer, setDebounceTimer] = useState(null);
    const [showError, setShowError] = useState(false);
    const scanInputRef = useRef(null);

    // Audit session state
    const [auditStarted, setAuditStarted] = useState(false);
    const [scannedItems, setScannedItems] = useState([]); // Items scanned during audit
    const [systemInventory, setSystemInventory] = useState([]); // What system says should be there
    const [comparisonResults, setComparisonResults] = useState({
        matched: [],
        missing: [], // In system but not scanned
        extra: []    // Scanned but not in system
    });
    const [finalizing, setFinalizing] = useState(false);
    const [finalizingProgress, setFinalizingProgress] = useState('');
    const [showCompletionModal, setShowCompletionModal] = useState(false);

    // Empty-a-pallet-space consolidation (owner, 2026-09-01). The preview is a server call
    // against the SAME query the real move runs, so the dialog's count can never disagree
    // with what actually moves.
    const [consolidateOpen, setConsolidateOpen] = useState(false);
    const [consolidateDest, setConsolidateDest] = useState('');
    const [consolidatePreview, setConsolidatePreview] = useState(null);
    const [consolidateBusy, setConsolidateBusy] = useState(false);

    const validLengths = [9, 10, 12, 13];

    // Fetch locations when component mounts
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const locationsResponse = await ApiClient.post("/api/sflocalinventorylocations");
                if (locationsResponse?.data) {
                    setLocations(locationsResponse.data);
                }
            } catch (error) {
                console.error("Error fetching locations:", error);
                setMessage({ type: 'error', text: 'Failed to load location options' });
            }
        };

        fetchLocations();

        const fetchOldestLocations = async () => {
            try {
                const resp = await ApiClient.get("/api/inventory/oldest-audit-locations", { params: { limit: 10 } });
                if (Array.isArray(resp?.data)) {
                    setOldestLocations(resp.data);
                }
            } catch (error) {
                console.error("Error fetching audit-priority locations:", error);
            }
        };

        fetchOldestLocations();
    }, []);

    // Auto-focus scan input
    useEffect(() => {
        if (scanInputRef.current && auditStarted) {
            scanInputRef.current.focus();
        }
    }, [auditStarted]);

    // Cleanup debounce timer
    useEffect(() => {
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [debounceTimer]);

    // Surface a consolidation refusal the same way the audit's own 409 guard is surfaced.
    const surfaceConsolidationError = (error, fallback) => {
        if (error.response?.status === 409 && error.response.data?.blocked) {
            setMessage({ type: 'error', text: error.response.data.message });
        } else if (error.response?.data?.error) {
            setMessage({ type: 'error', text: error.response.data.error });
        } else {
            setMessage({ type: 'error', text: fallback });
        }
    };

    const handleReviewMove = async () => {
        if (!selectedLocation || !consolidateDest) return;
        setConsolidateBusy(true);
        setConsolidatePreview(null);
        try {
            const res = await ApiClient.post('/api/inventory/empty-location', {
                from: selectedLocation, to: consolidateDest, preview: true,
            });
            setConsolidatePreview(res?.data || null);
        } catch (error) {
            // Owner ruling: sale_pending blocks — the server's refusal is the message.
            surfaceConsolidationError(error, 'Failed to preview the move');
        } finally {
            setConsolidateBusy(false);
        }
    };

    const handleConfirmMove = async () => {
        if (!selectedLocation || !consolidateDest) return;
        setConsolidateBusy(true);
        try {
            const res = await ApiClient.post('/api/inventory/empty-location', {
                from: selectedLocation, to: consolidateDest,
            });
            const moved = res?.data?.moved ?? 0;
            const skipped = res?.data?.skippedUnableToLocate ?? 0;
            setMessage({
                type: 'success',
                text: `Moved ${moved} units from ${selectedLocation} to ${consolidateDest}.`
                    + (skipped > 0
                        ? ` ${skipped} lost unit${skipped !== 1 ? 's' : ''} (unable to locate) stayed recorded at ${selectedLocation}.`
                        : ''),
            });
            setConsolidateOpen(false);
            setConsolidateDest('');
            setConsolidatePreview(null);
        } catch (error) {
            surfaceConsolidationError(error, 'Failed to move the units');
        } finally {
            setConsolidateBusy(false);
        }
    };

    // Start audit - fetch system inventory for location
    const handleStartAudit = async () => {
        if (!selectedLocation) {
            setMessage({ type: 'error', text: 'Please select a location first' });
            return;
        }

        setLoading(true);
        try {
            // Fetch current system inventory for this location
            const response = await ApiClient.post("/api/localinventorybylocation", {
                location: selectedLocation
            });

            if (response?.data) {
                setSystemInventory(response.data);
                setAuditStarted(true);
                setScannedItems([]);
                setMessage({
                    type: 'success',
                    text: `Audit started for ${selectedLocation}. Found ${response.data.length} items in system. Start scanning.`
                });
            }
        } catch (error) {
            console.error('Error starting audit:', error);
            // Guard: location has sale_pending units assigned to an order (HTTP 409).
            // Surface the server's explanation and do NOT start the audit.
            if (error.response?.status === 409 && error.response.data?.blocked) {
                setMessage({ type: 'error', text: error.response.data.message });
            } else {
                setMessage({ type: 'error', text: 'Failed to load inventory for location' });
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle scan input change with debounce
    const handleScanInputChange = (e) => {
        const value = e.target.value;
        setScanInput(value);

        setShowError(false);
        setMessage({ type: '', text: '' });

        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        const timer = setTimeout(() => {
            processScannedItem(value);
        }, 500);

        setDebounceTimer(timer);
    };

    // Process scanned item
    const processScannedItem = async (value) => {
        if (!value) return;

        // Validate length
        if (!validLengths.includes(value.length)) {
            setShowError(true);
            setMessage({ type: 'error', text: 'Invalid identifier length' });
            return;
        }

        if (value.startsWith("LPN")) {
            const errorTone = new Audio('/sounds/error-tone.mp3');
            errorTone.play();
            setScanInput('');
            setShowError(true);
            setMessage({ type: 'error', text: 'LPN codes are not supported' });
            return;
        }

        // Allow multiple scans of the same identifier (multiple units can have same FNSKU)
        // Each scan will be recorded as a separate line item with timestamp

        const successTone = new Audio('/sounds/success-tone.mp3');
        setLoading(true);

        try {
            // Look up item details
            const response = await ApiClient.post("/api/lookupidentifier", {
                identifier: value
            });

            if (response?.data) {
                const newItem = {
                    identifier: value,
                    ...response.data,
                    scannedAt: new Date().toLocaleString()
                };

                setScannedItems(prev => [...prev, newItem]);
                successTone.play();
                setScanInput('');
                setShowError(false);
                setMessage({
                    type: 'success',
                    text: `Scanned: ${value}${response.data.asin ? ` (${response.data.asin})` : ''}`
                });

                // Auto-focus for next scan
                setTimeout(() => {
                    if (scanInputRef.current) {
                        scanInputRef.current.focus();
                    }
                }, 100);
            }
        } catch (error) {
            console.error('Error looking up item:', error);
            // Even if lookup fails, add to scanned list
            const newItem = {
                identifier: value,
                scannedAt: new Date().toLocaleString()
            };
            setScannedItems(prev => [...prev, newItem]);
            successTone.play();
            setScanInput('');
            setMessage({ type: 'success', text: `Scanned: ${value} (details not found)` });
        } finally {
            setLoading(false);
        }
    };

    // Run comparison with quantity-based ID matching
    const handleRunComparison = () => {
        // Count how many times each identifier was scanned
        const scannedCounts = {};
        scannedItems.forEach(item => {
            scannedCounts[item.identifier] = (scannedCounts[item.identifier] || 0) + 1;
        });

        console.log('=== QUANTITY-BASED ID MATCHING ===');
        console.log('Scanned counts:', scannedCounts);

        // Build matched/missing records with partial ID lists
        const matchedRecords = [];
        const missingRecords = [];
        const extraRecords = [];

        systemInventory.forEach(systemItem => {
            const identifier = systemItem.identifier;
            const systemQuantity = systemItem.quantity;
            const scannedQuantity = scannedCounts[identifier] || 0;
            const idsArray = systemItem.ids.split(',').map(id => id.trim());

            console.log(`Processing ${identifier}: System qty=${systemQuantity}, Scanned qty=${scannedQuantity}, IDs=[${idsArray.join(', ')}]`);

            if (scannedQuantity > 0) {
                // Determine how many units can be matched (limited by system quantity)
                const matchCount = Math.min(scannedQuantity, systemQuantity);
                const matchedIds = idsArray.slice(0, matchCount);
                const missingIds = idsArray.slice(matchCount);

                if (matchedIds.length > 0) {
                    matchedRecords.push({
                        ...systemItem,
                        quantity: matchedIds.length,
                        ids: matchedIds.join(', ')
                    });
                    console.log(`  → Matched: ${matchedIds.length} units [${matchedIds.join(', ')}]`);
                }

                if (missingIds.length > 0) {
                    missingRecords.push({
                        ...systemItem,
                        quantity: missingIds.length,
                        ids: missingIds.join(', ')
                    });
                    console.log(`  → Missing: ${missingIds.length} units [${missingIds.join(', ')}]`);
                }

                // Handle over-scanned items (scanned more than system has)
                if (scannedQuantity > systemQuantity) {
                    const overCount = scannedQuantity - systemQuantity;
                    const scannedItem = scannedItems.find(item => item.identifier === identifier);
                    extraRecords.push({
                        identifier: identifier,
                        quantity: overCount,
                        asin: scannedItem?.asin || systemItem.asin || '',
                        seller_sku: scannedItem?.seller_sku || systemItem.seller_sku || ''
                    });
                    console.log(`  → Over-scanned: ${overCount} extra units beyond system quantity`);
                }

                // Remove this identifier from scannedCounts since we processed it
                delete scannedCounts[identifier];
            } else {
                // Not scanned at all - all units are missing
                missingRecords.push(systemItem);
                console.log(`  → Missing: ${systemQuantity} units (not scanned) [${idsArray.join(', ')}]`);
            }
        });

        // Extra items (scanned but not in system inventory at all)
        Object.entries(scannedCounts).forEach(([identifier, count]) => {
            const scannedItem = scannedItems.find(item => item.identifier === identifier);
            extraRecords.push({
                identifier: identifier,
                quantity: count,
                asin: scannedItem?.asin || '',
                seller_sku: scannedItem?.seller_sku || ''
            });
            console.log(`Extra item: ${identifier}, qty=${count}`);
        });

        const matchedCount = matchedRecords.reduce((sum, item) => sum + item.quantity, 0);
        const missingCount = missingRecords.reduce((sum, item) => sum + item.quantity, 0);
        const extraCount = extraRecords.reduce((sum, item) => sum + item.quantity, 0);

        console.log(`Final counts: Matched=${matchedCount}, Missing=${missingCount}, Extra=${extraCount}`);
        console.log('=== END QUANTITY-BASED ID MATCHING ===');

        setComparisonResults({
            matched: matchedRecords,
            missing: missingRecords,
            extra: extraRecords,
            matchedCount,
            missingCount,
            extraCount
        });
        setMessage({
            type: 'info',
            text: `Comparison complete: ${matchedCount} matched, ${missingCount} missing, ${extraCount} extra`
        });
    };

    // Finalize audit and update inventory
    const handleFinalizeAudit = async () => {
        if (comparisonResults.matched.length === 0 && comparisonResults.missing.length === 0 && comparisonResults.extra.length === 0) {
            setMessage({ type: 'error', text: 'Please run comparison first' });
            return;
        }

        const totalItems = comparisonResults.matched.length + comparisonResults.missing.length + comparisonResults.extra.length;
        const estimatedSeconds = Math.ceil((comparisonResults.missing.length * 0.5) + (comparisonResults.extra.length * 0.3) + 5);

        setFinalizing(true);
        setLoading(true);
        setFinalizingProgress(`Finalizing audit for ${totalItems} items... (Est. ${estimatedSeconds}s)`);

        try {
            // Send IDs with actions - cleaner structure
            // Extract IDs from each category for backend processing
            const matchedIds = comparisonResults.matched.flatMap(item =>
                item.ids.split(',').map(id => id.trim()).filter(id => id)
            );
            const missingIds = comparisonResults.missing.flatMap(item =>
                item.ids.split(',').map(id => id.trim()).filter(id => id)
            );

            // Extra items don't have IDs (not in system), send as records
            const response = await ApiClient.post("/api/finalizeaudit", {
                location: selectedLocation,
                matched: matchedIds,      // IDs to mark with last_audit_at
                missing: missingIds,      // IDs to mark as unable_to_locate
                extra: comparisonResults.extra  // Full records for items to insert
            }, {
                timeout: 120000  // 2 minute timeout instead of 30 seconds
            });

            if (response?.data?.success) {
                setFinalizingProgress('Audit complete! Inventory updated and emails sent.');
                setMessage({
                    type: 'success',
                    text: 'Audit finalized successfully. Inventory updated.'
                });
                // Show completion modal
                setShowCompletionModal(true);
            } else {
                throw new Error('Failed to finalize audit');
            }
        } catch (error) {
            console.error('Error finalizing audit:', error);
            setFinalizing(false);
            setFinalizingProgress('');
            if (error.code === 'ECONNABORTED') {
                setMessage({ type: 'error', text: 'Audit is taking longer than expected. Please check the audit history or contact support.' });
            } else {
                setMessage({ type: 'error', text: 'Failed to finalize audit' });
            }
        } finally {
            setLoading(false);
        }
    };

    // Cancel audit
    const handleCancelAudit = () => {
        setAuditStarted(false);
        setScannedItems([]);
        setSystemInventory([]);
        setComparisonResults({ matched: [], missing: [], extra: [] });
        setScanInput('');
        setMessage({ type: '', text: '' });
    };

    // Handle modal close
    const handleCloseCompletionModal = () => {
        setShowCompletionModal(false);
        setFinalizing(false);
        setFinalizingProgress('');
        handleCancelAudit();
    };

    // Remove scanned item by index (not by identifier, to allow removing individual items when duplicates exist)
    const handleRemoveScannedItem = (indexToRemove) => {
        const removedItem = scannedItems[indexToRemove];
        setScannedItems(prev => prev.filter((item, index) => index !== indexToRemove));
        setMessage({ type: 'info', text: `Removed ${removedItem.identifier} from scan list` });
    };

    return (
        <div className="space-y-6 px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">Location Inventory Audit</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Compare physical inventory against system records for a specific location.
                    </p>
                </div>
            </div>

            {/* Audit Priority: oldest locations that still hold inventory */}
            {!auditStarted && oldestLocations.length > 0 && (
                <div className="bg-white shadow rounded-lg mb-4">
                    <div className="px-4 py-5 sm:p-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-1">
                            Audit Priority — Oldest Locations with Inventory
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">
                            Top 10 locations holding stock, never-audited first then oldest. Click a row to select it.
                        </p>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Units</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Audited</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {oldestLocations.map((loc) => {
                                        const isPending = loc.pending_count > 0;
                                        return (
                                        <tr
                                            key={loc.location}
                                            onClick={() => { if (!isPending) setSelectedLocation(loc.location); }}
                                            title={isPending ? 'Has a pending order — ship or reassign it before auditing this location' : undefined}
                                            className={isPending
                                                ? 'opacity-60 cursor-not-allowed'
                                                : `cursor-pointer hover:bg-indigo-50 ${selectedLocation === loc.location ? 'bg-indigo-50' : ''}`}
                                        >
                                            <td className="px-3 py-2 text-sm font-medium text-gray-900">
                                                {loc.location}
                                                {isPending && (
                                                    <span className="ml-2 inline-flex items-center rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                                                        Pending order
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-gray-700">{loc.units_onhand}</td>
                                            <td className="px-3 py-2 text-sm">
                                                {loc.last_audited
                                                    ? <span className="text-gray-700">{new Date(loc.last_audited).toLocaleDateString()}</span>
                                                    : <span className="font-semibold text-red-600">
                                                        Never{loc.oldest_unit_added ? ` · added ${new Date(loc.oldest_unit_added).toLocaleDateString()}` : ''}
                                                      </span>}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Location Selection and Audit Start */}
            {!auditStarted && (
                <div className="bg-white shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Start Location Audit</h3>
                        <div className="space-y-4">
                            <div className="max-w-xs">
                                <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                                    Select Location to Audit
                                </label>
                                <select
                                    id="location"
                                    value={selectedLocation}
                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                >
                                    <option value="">Choose a location...</option>
                                    {locations.map((loc) => (
                                        <option key={loc.id} value={loc.location}>
                                            {loc.location}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={handleStartAudit}
                                disabled={!selectedLocation || loading}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                            >
                                {loading ? 'Loading...' : 'Start Audit'}
                            </button>

                            {/* Empty-a-pallet-space consolidation (owner, 2026-09-01):
                                sale_pending blocks (the server 409 is surfaced verbatim), and
                                moving never stamps last_audit_at — moving consolidates,
                                auditing verifies. */}
                            <div className="pt-3 border-t border-gray-100">
                                <button
                                    onClick={() => { setConsolidateOpen(v => !v); setConsolidatePreview(null); }}
                                    disabled={!selectedLocation || loading}
                                    className="inline-flex items-center px-3 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-800 bg-white hover:bg-amber-50 disabled:opacity-50"
                                    title="Move every unit at this location to another pallet space, leaving this one empty. Units awaiting pick for an order block the move."
                                >
                                    Empty this pallet space…
                                </button>
                                {consolidateOpen && selectedLocation && (
                                    <div className="mt-3 space-y-3 max-w-md">
                                        <div>
                                            <label htmlFor="consolidate-dest" className="block text-sm font-medium text-gray-700">
                                                Move everything to
                                            </label>
                                            <select
                                                id="consolidate-dest"
                                                value={consolidateDest}
                                                onChange={(e) => { setConsolidateDest(e.target.value); setConsolidatePreview(null); }}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm"
                                            >
                                                <option value="">Choose destination…</option>
                                                {locations
                                                    .filter((loc) => loc.location !== selectedLocation)
                                                    .map((loc) => (
                                                        <option key={loc.id} value={loc.location}>
                                                            {loc.location}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                        {!consolidatePreview ? (
                                            <button
                                                onClick={handleReviewMove}
                                                disabled={!consolidateDest || consolidateBusy}
                                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                                            >
                                                {consolidateBusy ? 'Checking…' : 'Review move'}
                                            </button>
                                        ) : (
                                            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm space-y-1">
                                                <p className="font-medium text-amber-900">
                                                    Move {consolidatePreview.wouldMove} units from {selectedLocation} → {consolidateDest}, leaving {selectedLocation} empty.
                                                </p>
                                                {(consolidatePreview.skippedUnableToLocate || 0) > 0 && (
                                                    <p className="text-amber-800">
                                                        {consolidatePreview.skippedUnableToLocate} lost unit
                                                        {consolidatePreview.skippedUnableToLocate !== 1 ? 's' : ''} (unable to locate) will stay recorded at {selectedLocation}.
                                                    </p>
                                                )}
                                                <p className="text-xs text-gray-600">
                                                    Moving consolidates — it does not audit. The destination keeps its audit age.
                                                </p>
                                                <button
                                                    onClick={handleConfirmMove}
                                                    disabled={consolidateBusy}
                                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                                                >
                                                    {consolidateBusy ? 'Moving…' : 'Confirm move'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Audit Session */}
            {auditStarted && (
                <>
                    {/* Audit Header */}
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-medium text-indigo-900">
                                    Active Audit: {selectedLocation}
                                </h3>
                                <p className="text-sm text-indigo-700 mt-1">
                                    System Inventory: {systemInventory.length} items | Scanned: {scannedItems.length} items
                                </p>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleRunComparison}
                                    disabled={scannedItems.length === 0}
                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                >
                                    Run Comparison
                                </button>
                                <button
                                    onClick={handleCancelAudit}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Cancel Audit
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Scan Input */}
                    <div className="bg-white shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Scan Items</h3>
                            <div className="relative">
                                <label htmlFor="scan-input" className="block text-sm font-medium text-gray-700">
                                    Scan or Enter Identifier
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        ref={scanInputRef}
                                        type="text"
                                        id="scan-input"
                                        className={`block w-full rounded-md border-0 py-1.5 pr-10 ${
                                            showError ? 'text-red-900 ring-red-300' : 'text-gray-900 ring-gray-300'
                                        } ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset ${
                                            showError ? 'focus:ring-red-500' : 'focus:ring-indigo-500'
                                        } sm:text-sm sm:leading-6`}
                                        placeholder="Scan identifier here"
                                        value={scanInput}
                                        onChange={handleScanInputChange}
                                    />
                                    {showError && (
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                                        </div>
                                    )}
                                    {loading && (
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Scanned Items List */}
                    <div className="bg-white shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                                Scanned Items ({scannedItems.length})
                            </h3>
                            {scannedItems.length === 0 ? (
                                <p className="text-sm text-gray-500">No items scanned yet. Start scanning to add items.</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead>
                                            <tr>
                                                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Identifier</th>
                                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">ASIN</th>
                                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">SKU</th>
                                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Scanned At</th>
                                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {scannedItems.slice().reverse().map((item, reversedIndex) => {
                                                const index = scannedItems.length - 1 - reversedIndex;
                                                return (
                                                <tr key={index}>
                                                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                        {item.identifier}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {item.asin || '-'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {item.seller_sku || '-'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {item.scannedAt}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                        <button
                                                            onClick={() => handleRemoveScannedItem(index)}
                                                            className="text-red-600 hover:text-red-900"
                                                        >
                                                            Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Comparison Results */}
                    {(comparisonResults.matched.length > 0 || comparisonResults.missing.length > 0 || comparisonResults.extra.length > 0) && (
                        <div className="space-y-6">
                            <div className="bg-white shadow rounded-lg">
                                <div className="px-4 py-5 sm:p-6">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Comparison Results</h3>

                                    {/* Summary Cards */}
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
                                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                            <div className="flex items-center">
                                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-green-900">Matched</p>
                                                    <p className="text-2xl font-bold text-green-600">{comparisonResults.matchedCount ?? 0}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                            <div className="flex items-center">
                                                <MinusCircleIcon className="h-8 w-8 text-red-600" />
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-red-900">Missing</p>
                                                    <p className="text-2xl font-bold text-red-600">{comparisonResults.missingCount ?? 0}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                                            <div className="flex items-center">
                                                <XCircleIcon className="h-8 w-8 text-orange-600" />
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-orange-900">Extra</p>
                                                    <p className="text-2xl font-bold text-orange-600">{comparisonResults.extraCount ?? 0}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Matched Items */}
                                    {comparisonResults.matched.length > 0 && (
                                        <div className="mb-6">
                                            <h4 className="text-md font-medium text-green-900 mb-2 flex items-center">
                                                <CheckCircleIcon className="h-5 w-5 mr-2" />
                                                Matched Items ({comparisonResults.matchedCount ?? 0} units)
                                            </h4>
                                            <div className="bg-green-50 p-3 rounded text-sm text-green-800">
                                                {comparisonResults.matched.map(item => item.identifier).join(', ')}
                                            </div>
                                        </div>
                                    )}

                                    {/* Missing Items */}
                                    {comparisonResults.missing.length > 0 && (
                                        <div className="mb-6">
                                            <h4 className="text-md font-medium text-red-900 mb-2 flex items-center">
                                                <MinusCircleIcon className="h-5 w-5 mr-2" />
                                                Missing Items - In System But Not Scanned ({comparisonResults.missingCount ?? 0} units)
                                            </h4>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-red-200">
                                                    <thead className="bg-red-50">
                                                        <tr>
                                                            <th className="py-2 pl-4 pr-3 text-left text-xs font-medium text-red-900">IDs</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-red-900">Identifier</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-red-900">ASIN</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-red-900">SKU</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-red-900">Qty</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-red-100 bg-red-50">
                                                        {comparisonResults.missing.map((item, index) => (
                                                            <tr key={index}>
                                                                <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-red-700">
                                                                    {item.ids || '-'}
                                                                </td>
                                                                <td className="whitespace-nowrap px-3 py-2 text-sm text-red-900">
                                                                    {item.identifier}
                                                                </td>
                                                                <td className="whitespace-nowrap px-3 py-2 text-sm text-red-700">
                                                                    {item.asin || '-'}
                                                                </td>
                                                                <td className="whitespace-nowrap px-3 py-2 text-sm text-red-700">
                                                                    {item.seller_sku || '-'}
                                                                </td>
                                                                <td className="whitespace-nowrap px-3 py-2 text-sm font-semibold text-red-900">
                                                                    {item.quantity || 1}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Extra Items */}
                                    {comparisonResults.extra.length > 0 && (
                                        <div className="mb-6">
                                            <h4 className="text-md font-medium text-orange-900 mb-2 flex items-center">
                                                <XCircleIcon className="h-5 w-5 mr-2" />
                                                Extra Items - Scanned But Not In System ({comparisonResults.extraCount ?? 0} units)
                                            </h4>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-orange-200">
                                                    <thead className="bg-orange-50">
                                                        <tr>
                                                            <th className="py-2 pl-4 pr-3 text-left text-xs font-medium text-orange-900">Identifier</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-orange-900">ASIN</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-orange-900">SKU</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-orange-100 bg-orange-50">
                                                        {comparisonResults.extra.map((item, index) => (
                                                            <tr key={index}>
                                                                <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-orange-900">
                                                                    {item.identifier}
                                                                </td>
                                                                <td className="whitespace-nowrap px-3 py-2 text-sm text-orange-700">
                                                                    {item.asin || '-'}
                                                                </td>
                                                                <td className="whitespace-nowrap px-3 py-2 text-sm text-orange-700">
                                                                    {item.seller_sku || '-'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Finalize Button */}
                                    <div className="mt-6 flex justify-end">
                                        <button
                                            onClick={handleFinalizeAudit}
                                            disabled={loading || finalizing}
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                        >
                                            {loading || finalizing ? 'Finalizing...' : 'Finalize Audit & Update Inventory'}
                                        </button>
                                    </div>

                                    {/* Progress Indicator */}
                                    {finalizing && finalizingProgress && (
                                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                                            <div className="flex items-center">
                                                <svg className="animate-spin h-5 w-5 text-blue-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <div>
                                                    <p className="text-sm font-medium text-blue-800">{finalizingProgress}</p>
                                                    <p className="text-xs text-blue-600 mt-1">Please wait, processing inventory updates...</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Message Display */}
            {message.text && (
                <div className={`rounded-md p-4 ${
                    message.type === 'success'
                        ? 'bg-green-50 border border-green-200'
                        : message.type === 'error'
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-blue-50 border border-blue-200'
                }`}>
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className={`text-sm font-medium ${
                                message.type === 'success' ? 'text-green-800' : message.type === 'error' ? 'text-red-800' : 'text-blue-800'
                            }`}>
                                {message.text}
                            </h3>
                        </div>
                    </div>
                </div>
            )}

            {/* Completion Modal */}
            {showCompletionModal && (
                <div className="fixed z-10 inset-0 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
                            <div>
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                                    <CheckCircleIcon className="h-6 w-6 text-green-600" />
                                </div>
                                <div className="mt-3 text-center sm:mt-5">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                                        Audit for {selectedLocation} is Complete
                                    </h3>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            The inventory audit has been successfully finalized and email notifications have been sent.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-6">
                                <button
                                    type="button"
                                    className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                                    onClick={handleCloseCompletionModal}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LocalInventoryAudit;
